import { Icon } from '../../components/Brand'
import { money, number } from '../api'

export function PageHeader({ eyebrow = 'Collections', title, description, actions, meta }) {
  return (
    <div className="col-page-head">
      <div>
        <div className="col-eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      <div className="col-head-actions">{meta}{actions}</div>
    </div>
  )
}

export function StatePanel({ loading, error, empty, emptyTitle = 'Nothing here yet', emptyText, onRetry, children }) {
  if (loading) {
    return (
      <div className="col-state col-loading" aria-live="polite">
        <span className="col-spinner" />
        <div><b>Loading Collections data</b><span>Fetching the latest available state…</span></div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="col-state col-error" role="alert">
        <div className="col-state-icon"><Icon name="cloud_off" /></div>
        <div><b>We couldn’t load this view</b><span>{error.message || 'The Collections API is unavailable.'}</span></div>
        {onRetry && <button className="col-btn secondary" onClick={onRetry}><Icon name="refresh" />Retry</button>}
      </div>
    )
  }
  if (empty) {
    return (
      <div className="col-state col-empty">
        <div className="col-state-icon"><Icon name="inbox" /></div>
        <div><b>{emptyTitle}</b><span>{emptyText || 'Adjust the filters or check back after the next workflow run.'}</span></div>
      </div>
    )
  }
  return children
}

export function MetricCard({ label, value, icon, tone = 'coral', detail, format = 'number', onClick }) {
  const shown = format === 'money' ? money(value, 'ILS', true) : format === 'percent' ? `${number(value, 1)}%` : format === 'days' ? `${number(value, 1)}d` : number(value)
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag className={`col-metric ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <span className={`col-metric-icon ${tone}`}><Icon name={icon} /></span>
      <span className="col-metric-label">{label}</span>
      <strong>{shown}</strong>
      {detail && <span className="col-metric-detail">{detail}</span>}
    </Tag>
  )
}

export function Badge({ children, tone = 'neutral' }) {
  return <span className={`col-badge ${tone}`}>{children || '—'}</span>
}

export function SectionTitle({ title, text, action }) {
  return (
    <div className="col-section-head">
      <div><h2>{title}</h2>{text && <p>{text}</p>}</div>
      {action}
    </div>
  )
}

export function AgingBars({ values = {}, percentages = false }) {
  const entries = Object.entries(values)
  const max = Math.max(...entries.map(([, value]) => Number(value || 0)), 1)
  if (!entries.length) return null
  return (
    <div className="col-aging-bars">
      {entries.map(([label, value], index) => (
        <div className="col-aging-row" key={label}>
          <span>{label}</span>
          <div><i style={{ width: `${Math.max(2, Number(value || 0) / max * 100)}%` }} data-index={index} /></div>
          <b>{percentages ? `${number(value, 1)}%` : money(value, 'ILS', true)}</b>
        </div>
      ))}
    </div>
  )
}

export function ExportButton({ onClick, busy = false }) {
  return <button className="col-btn secondary" onClick={onClick} disabled={busy}><Icon name="download" />{busy ? 'Preparing…' : 'Export .xlsx'}</button>
}

export function Pagination({ offset = 0, limit = 25, total = 0, onChange }) {
  if (!total) return null
  const start = offset + 1
  const end = Math.min(offset + limit, total)
  return <nav className="col-pagination" aria-label="Pagination">
    <span>{start}–{end} of {total}</span>
    <div>
      <button className="col-mini-action" disabled={offset <= 0} onClick={() => onChange(Math.max(0, offset - limit))}><Icon name="chevron_left" />Previous</button>
      <button className="col-mini-action" disabled={end >= total} onClick={() => onChange(offset + limit)}>Next<Icon name="chevron_right" /></button>
    </div>
  </nav>
}
